import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "./cn";

/**
 * The list surface the dashboard and Posts pages use: a rounded card holding a
 * header row of small uppercase labels, then rows divided by hairlines that
 * tint on hover. Columns are a CSS grid template shared by header and rows, so
 * they line up without a <table>. Below md the header hides and each row
 * stacks.
 */
export function DataTable({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      role="table"
      className={cn(
        "overflow-hidden rounded-xl border border-border/50 bg-card",
        className
      )}
      {...props}
    />
  );
}

/** Header row. `columns` is a grid-template-columns value, e.g. "1fr 120px". */
export function DataTableHead({
  columns,
  children,
  className,
}: {
  columns: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      role="row"
      style={{ gridTemplateColumns: columns }}
      className={cn(
        "hidden items-center gap-4 border-b border-border/30 px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground md:grid",
        className
      )}
    >
      {children}
    </div>
  );
}

export function DataTableBody({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("divide-y divide-border/30", className)} {...props} />
  );
}

/** A row; `onClick` makes it a clickable one. */
export function DataTableRow({
  columns,
  onClick,
  children,
  className,
}: {
  columns: string;
  onClick?: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      role="row"
      style={{ gridTemplateColumns: columns }}
      onClick={onClick}
      className={cn(
        "flex flex-col gap-2 px-4 py-4 transition-colors hover:bg-accent/30 md:grid md:items-center md:gap-4 md:px-6",
        onClick && "cursor-pointer",
        className
      )}
    >
      {children}
    </div>
  );
}

/*
 * The same surface for tabular data that is genuinely a table (sortable,
 * many columns, row links). Real <table> elements, so screen readers get the
 * semantics; the look is the dashboard's: uppercase small header, hairline
 * rows, hover tint.
 */

export function Table({
  className,
  ...props
}: HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="overflow-x-auto">
      <table className={cn("w-full text-sm", className)} {...props} />
    </div>
  );
}

export function THead({
  className,
  ...props
}: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead
      className={cn(
        "border-b border-border/30 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground",
        className
      )}
      {...props}
    />
  );
}

export function TBody({
  className,
  ...props
}: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody className={cn("divide-y divide-border/30", className)} {...props} />
  );
}

export function TR({
  className,
  ...props
}: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      className={cn("transition-colors hover:bg-accent/30", className)}
      {...props}
    />
  );
}

export function TH({
  className,
  ...props
}: HTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn("px-4 py-3 font-medium first:pl-6 last:pr-6", className)}
      {...props}
    />
  );
}

export function TD({
  className,
  ...props
}: HTMLAttributes<HTMLTableCellElement>) {
  return (
    <td
      className={cn("px-4 py-3 first:pl-6 last:pr-6", className)}
      {...props}
    />
  );
}
